/**
 * Flavour-driven media on the hydration PDP.
 *
 * The PDP is a CONTAINER product: it carries almost no product data of its own
 * (no description, no measurements, no nutrition panel, and only three media,
 * one of which is a single flavour's pack shot). Everything real lives on the
 * combined flavour products, and until now only the buy box read them, so the
 * page showed one fixed flavour no matter what the customer picked.
 *
 * This wires the pieces whose per-flavour source actually exists today:
 *
 *   - The Nutrition Panel popup image, from each tile's own
 *     data-nip-src / data-nip-srcset (custom.ingredients_nutrition_panel on the
 *     flavour product).
 *
 * Plus one thing that is not flavour-driven but belongs to the same "popup
 * content comes from elsewhere" job:
 *
 *   - The How To Use popup mirrors the How To Use section further down the
 *     page, rather than duplicating its copy into a block setting. Cloned in JS
 *     because that section owns its own settings and blocks, which a block in
 *     the product-info section cannot read.
 *
 * Which flavour wins: up to two can be selected, and everything flavour-driven
 * on the page (this popup, the tier cards, the payoff sachet and the gallery)
 * follows the MOST RECENT pick, the last entry in the flavours-change event's
 * `selections`, so the page always shows the flavour the customer just tapped
 * and no two parts disagree. With nothing selected the server-rendered default
 * stays, which is the first flavour with a purchasable variant (the same one
 * the buy box defaults to).
 *
 * The gallery (main Splide carousel + thumbnail slider + zoom lightbox) is
 * now also flavour-driven, but lives in its own swap script in
 * snippets/hydration-info-gallery.liquid instead of here — it needs direct
 * access to the Splide/Swiper instances that script already owns, and
 * rebuilding three synced sliders is a different job than swapping one
 * <img>'s src. It follows the same most-recent-pick rule.
 *
 * The payoff section's sachet and glow follow the picks too (syncPayoffSachet).
 * Deliberately NOT handled here: the benefits image, which has no per-flavour
 * asset yet.
 */
(() => {
  // The tile for the most recent pick. See the note in the header.
  function latestTile(event) {
    const selections = (event && event.detail && event.detail.selections) || [];
    const latest = selections.length ? selections[selections.length - 1].flavourId : null;
    return latest ? document.querySelector(`.supply-buybox__flavour[data-flavour-id="${latest}"]`) : null;
  }

  function syncNutritionPanel(event) {
    const target = document.querySelector('[data-flavour-nutrition-image]');
    if (!target) return;

    // Remember the server-rendered default so deselecting everything restores
    // it rather than leaving the last-picked flavour's panel on screen.
    if (!target.dataset.defaultSrc) {
      target.dataset.defaultSrc = target.getAttribute('src') || '';
      target.dataset.defaultSrcset = target.getAttribute('srcset') || '';
      target.dataset.defaultAlt = target.getAttribute('alt') || '';
    }

    const tile = latestTile(event);
    const src = tile && tile.dataset.nipSrc;

    if (src) {
      target.setAttribute('src', src);
      target.setAttribute('srcset', tile.dataset.nipSrcset || '');
      const name = tile.dataset.flavourName;
      if (name) target.setAttribute('alt', `${name} nutrition panel`);
    } else {
      // Either nothing is selected, or the selected flavour has no panel of its
      // own — fall back rather than blanking the image.
      target.setAttribute('src', target.dataset.defaultSrc);
      target.setAttribute('srcset', target.dataset.defaultSrcset);
      target.setAttribute('alt', target.dataset.defaultAlt);
    }
  }

  // The How To Use section is rendered once, further down the page. Clone it
  // rather than move it, so the section itself keeps working where it is.
  function mirrorHowToUse() {
    const mirror = document.querySelector('[data-howto-mirror]');
    if (!mirror || mirror.dataset.filled === 'true') return;

    const source = document.querySelector('[id^="HowToUse-"] .how-to-use__grid');
    if (!source) return; // section not on this template; the fallback image stays

    const clone = source.cloneNode(true);
    // The popup is copy only: the section's photo stays on the page but not
    // in here (Jono, 24 Sep).
    clone.querySelectorAll('.how-to-use__media').forEach((el) => el.remove());
    // Ids would be duplicated by the clone, and the section's own lazyload
    // classes would re-trigger against an element the observer never saw.
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('img').forEach((img) => {
      // The section's image is lazyloaded: its real URLs sit in data-src /
      // data-srcset while src holds a placeholder pixel. The clone is never
      // seen by the lazyload observer, so promote them by hand or the popup
      // shows an empty grey box.
      const dataSrc = img.getAttribute('data-src');
      const dataSrcset = img.getAttribute('data-srcset');
      if (dataSrc) img.setAttribute('src', dataSrc);
      if (dataSrcset) img.setAttribute('srcset', dataSrcset);
      img.classList.remove('lazyload', 'no-js-hidden');
      img.removeAttribute('loading');
    });

    mirror.appendChild(clone);
    mirror.dataset.filled = 'true';

    // The clone is the real content now, so drop the placeholder image.
    const fallback = document.querySelector('[data-howto-fallback]');
    if (fallback) fallback.remove();
  }

  // Supply tier artwork. The "Flavour artwork" blocks in
  // sections/hydration-product-info.liquid list each flavour's 1, 2 and 3 month
  // images in [data-hydration-flavour-art]; the tier cards swap to the picked
  // flavour's set so the pouch count on each card shows that flavour.
  //
  // Which flavour: the most recent pick, not tile order. That is the rule the
  // gallery follows (see the note on its listener in
  // snippets/hydration-info-gallery.liquid), and the tier art and the gallery
  // should never disagree about which flavour is on screen.
  function flavourArt(flavourId) {
    if (!flavourId) return null;
    return document.querySelector(`[data-hydration-flavour-art] [data-flavour-art="${flavourId}"]`);
  }

  // The list stores every URL at width 1500; ask the CDN for the size needed.
  function atWidth(url, width) {
    return url.replace(/([?&]width=)\d+/, `$1${width}`);
  }

  // With exactly two flavours picked, a "Flavour pair image" block can carry
  // half-and-half supply images for that pair (either order).
  function pairArt(selections) {
    if (selections.length !== 2) return null;
    const a = String(selections[0].flavourId);
    const b = String(selections[1].flavourId);
    return Array.from(document.querySelectorAll('[data-hydration-flavour-art] [data-flavour-pair]')).find((el) => {
      const ids = el.dataset.flavourPair.split('|');
      return (ids[0] === a && ids[1] === b) || (ids[0] === b && ids[1] === a);
    }) || null;
  }

  function syncTierImages(event) {
    const selections = (event && event.detail && event.detail.selections) || [];
    const latest = selections.length ? selections[selections.length - 1].flavourId : null;
    const pair = pairArt(selections);
    const single = flavourArt(latest);
    const names = selections.map((sel) => {
      const el = flavourArt(sel.flavourId);
      return el ? el.dataset.flavourName : '';
    }).filter(Boolean);
    // The pair's images when it has one for the tier, otherwise the latest
    // pick's own set (resolved per card below).
    const art = single;
    const name = pair && names.length === 2 ? names.join(' and ') : (single ? single.dataset.flavourName : '');

    document.querySelectorAll('.supply-buybox__tier-image').forEach((img) => {
      // Remember the block-setting image so clearing the picks, or picking a
      // flavour with no artwork set, puts it back instead of leaving the last
      // flavour's pouches on the card.
      if (!img.dataset.defaultSrc) {
        img.dataset.defaultSrc = img.getAttribute('src') || '';
        img.dataset.defaultAlt = img.getAttribute('alt') || '';
      }

      const radio = img.closest('.supply-buybox__tier-wrapper');
      const months = radio && radio.querySelector('[data-multiplier]')
        ? radio.querySelector('[data-multiplier]').dataset.multiplier
        : null;
      const pairUrl = pair && months ? pair.getAttribute(`data-tier-${months}`) : null;
      const url = pairUrl || (art && months ? art.getAttribute(`data-tier-${months}`) : null);

      if (url) {
        // 400px covers the widest card (165px on desktop) at 2x.
        img.setAttribute('src', atWidth(url, 400));
        img.setAttribute('alt', `${months} Month Supply${name ? ` of ${name}` : ''}`);
      } else {
        img.setAttribute('src', img.dataset.defaultSrc);
        img.setAttribute('alt', img.dataset.defaultAlt);
      }
    });
  }

  // Payoff section sachet and glow. Each "Flavour artwork" block can carry a
  // sachet image and a glow colour. One flavour picked shows its sachet; two
  // picked (or the variety pack, which has no sachet of its own and stands for
  // every flavour) rotate between them. Nothing picked rotates through every
  // flavour too, starting once the section scrolls near the viewport so the
  // sachet images are not fetched for shoppers who never get that far.
  //
  // The sachet art is not all on one canvas: the section's default is an
  // upright stick centred on a square, the flavour sachets are tilted sticks
  // right of centre on a portrait canvas. Each image's opaque area is measured
  // once (the CDN sends CORS headers, so a canvas can read it) and the image is
  // scaled and shifted so its stick matches the default's height and position.
  // The shift is in percent of the image's own box, so it holds at any size.
  const SACHET_ROTATE_MS = 2600;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sachetBoxes = new Map();
  let sachetTimer = null;
  let sachetToken = 0;
  // Swaps are skipped while the section is off screen; nobody sees them, and
  // it saves fetching sachet art mid-scroll elsewhere on the page.
  let payoffOnScreen = false;
  let payoffStarted = false;

  function payoffImage() {
    return document.querySelector('.payoff-benefits .payoff-media__image');
  }

  // Opaque bounding box as fractions of the canvas, plus the canvas aspect.
  function measureSachet(url) {
    if (sachetBoxes.has(url)) return sachetBoxes.get(url);
    const job = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          const w = probe.naturalWidth;
          const h = probe.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(probe, 0, 0);
          const data = ctx.getImageData(0, 0, w, h).data;
          let x0 = w, y0 = h, x1 = -1, y1 = -1;
          for (let y = 0; y < h; y += 1) {
            for (let x = 0; x < w; x += 1) {
              if (data[(y * w + x) * 4 + 3] > 16) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
              }
            }
          }
          if (x1 < 0) return resolve(null);
          resolve({
            aspect: w / h,
            cx: (x0 + x1 + 1) / 2 / w,
            cy: (y0 + y1 + 1) / 2 / h,
            height: (y1 - y0 + 1) / h,
          });
        } catch (error) {
          resolve(null); // tainted canvas or similar: show the art unadjusted
        }
      };
      probe.onerror = () => resolve(null);
      probe.src = atWidth(url, 100);
    });
    sachetBoxes.set(url, job);
    return job;
  }

  function sachetList(selections) {
    const withSachet = [...document.querySelectorAll('[data-hydration-flavour-art] [data-sachet]')];
    // Nothing picked: every flavour.
    if (!selections.length) return withSachet;
    const list = [];
    const add = (art) => {
      if (!list.some((item) => item.dataset.sachet === art.dataset.sachet)) list.push(art);
    };
    // Latest pick first, so the flavour just tapped is the one that appears.
    selections.slice().reverse().forEach((selection) => {
      const art = flavourArt(selection.flavourId);
      if (!art) return;
      if (art.dataset.sachet) add(art);
      else withSachet.forEach(add); // variety pack
    });
    return list;
  }

  async function showSachet(img, art, token) {
    const section = img.closest('.payoff-benefits');
    const src = art ? art.dataset.sachet : img.dataset.defaultSrc;
    const [box, base] = await Promise.all([
      art ? measureSachet(src) : null,
      measureSachet(img.dataset.defaultSrc),
    ]);
    // Preload so the swap does not flash an empty box. Load event, not
    // decode(): decode() can stay pending while the page is in the background,
    // and the timeout means a slow image still swaps rather than never.
    await new Promise((resolve) => {
      const next = new Image();
      next.onload = resolve;
      next.onerror = resolve;
      setTimeout(resolve, 1500);
      next.src = atWidth(src, 500);
    });
    if (token !== sachetToken) return;

    img.classList.add('is-swapping');
    await new Promise((resolve) => setTimeout(resolve, reducedMotion.matches ? 0 : 250));
    if (token !== sachetToken) return;

    if (art) {
      img.setAttribute('src', atWidth(src, 500));
      img.setAttribute('srcset', `${atWidth(src, 250)} 250w, ${atWidth(src, 500)} 500w`);
      img.setAttribute('alt', `${art.dataset.flavourName} HYDRATION sachet`);
    } else {
      img.setAttribute('src', img.dataset.defaultSrc);
      img.setAttribute('srcset', img.dataset.defaultSrcset);
      img.setAttribute('alt', img.dataset.defaultAlt);
    }

    if (box && base) {
      const scale = base.height / box.height;
      const shiftX = (base.cx - 0.5) * (base.aspect / box.aspect) - (box.cx - 0.5) * scale;
      const shiftY = (base.cy - 0.5) - (box.cy - 0.5) * scale;
      img.style.setProperty('--payoff-scale', scale.toFixed(4));
      img.style.setProperty('--payoff-shift', `${(shiftX * 100).toFixed(2)}%`);
      img.style.setProperty('--payoff-shift-y', `${(shiftY * 100).toFixed(2)}%`);
    } else {
      ['--payoff-scale', '--payoff-shift', '--payoff-shift-y'].forEach((name) => img.style.removeProperty(name));
    }

    if (section) {
      if (art && art.dataset.glow) section.style.setProperty('--payoff-glow', art.dataset.glow);
      else section.style.removeProperty('--payoff-glow');
    }

    img.classList.remove('is-swapping');
  }

  function syncPayoffSachet(event) {
    const img = payoffImage();
    if (!img) return;
    if (!img.dataset.defaultSrc) {
      img.dataset.defaultSrc = img.getAttribute('src') || '';
      img.dataset.defaultSrcset = img.getAttribute('srcset') || '';
      img.dataset.defaultAlt = img.getAttribute('alt') || '';
    }

    payoffStarted = true;
    const selections = (event && event.detail && event.detail.selections) || [];
    const list = sachetList(selections);
    sachetToken += 1;
    const token = sachetToken;
    clearInterval(sachetTimer);
    sachetTimer = null;

    showSachet(img, list[0] || null, token);

    if (list.length > 1 && !reducedMotion.matches) {
      let index = 0;
      sachetTimer = setInterval(() => {
        if (document.hidden || !payoffOnScreen) return;
        index = (index + 1) % list.length;
        showSachet(img, list[index], token);
      }, SACHET_ROTATE_MS);
    }
  }

  // "View full ingredients + nutrition" in the ingredients section opens the
  // same Nutrition Panel popup as the buy box, so it follows the picked flavour.
  // Deferred a tick: the popup closes on any body click outside itself, and
  // this click is still bubbling towards body when the popup opens.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-open-nutrition-panel]');
    if (!link) return;
    const image = document.querySelector('[data-flavour-nutrition-image]');
    const summary = image && image.closest('details') && image.closest('details').querySelector('summary');
    if (!summary) return; // no popup on this page: let the link's URL apply
    event.preventDefault();
    setTimeout(() => summary.click(), 0);
  });

  // Tracks whether the payoff section is near the viewport, and starts the
  // no-pick rotation the first time it is (unless a pick already started it).
  function watchPayoff() {
    const media = document.querySelector('.payoff-benefits .payoff-media');
    if (!media) return;
    if (!('IntersectionObserver' in window)) {
      payoffOnScreen = true;
      if (!payoffStarted) syncPayoffSachet();
      return;
    }
    new IntersectionObserver((entries) => {
      payoffOnScreen = entries.some((entry) => entry.isIntersecting);
      if (payoffOnScreen && !payoffStarted) syncPayoffSachet();
    }, { rootMargin: '300px 0px' }).observe(media);
  }

  // Judge.me's preview badge under the gallery reads "24 reviews" (its admin
  // template). The design (Figma 8915:3414 / 8919:6102) reads "4.9 Based on
  // 24 Reviews": the average in bold, then the count underlined. The badge
  // carries both numbers as data attributes, so rebuild the text from them.
  // Judge.me can re-render the badge after load, so a mutation watcher puts
  // it back; the data-hydration-formatted marker stops it re-firing on its
  // own change (together with the check that our markup is still in place).
  function formatReviewBadge() {
    document.querySelectorAll('#product-header .jdgm-prev-badge').forEach((badge) => {
      const text = badge.querySelector('.jdgm-prev-badge__text');
      const average = parseFloat(badge.dataset.averageRating);
      const count = parseInt(badge.dataset.numberOfReviews, 10);
      if (!text || !count || Number.isNaN(average)) return;
      const key = `${average}|${count}`;
      // Judge.me rewrites this span's contents in place, which leaves the
      // marker behind, so also check our markup is still there.
      if (text.dataset.hydrationFormatted === key && text.querySelector('.jdgm-prev-badge__avg')) return;
      text.dataset.hydrationFormatted = key;
      text.innerHTML = `<strong class="jdgm-prev-badge__avg">${average.toFixed(1)}</strong> `
        + `<span class="jdgm-prev-badge__count">Based on ${count} Review${count === 1 ? '' : 's'}</span>`;
    });
  }

  function watchReviewBadge() {
    const header = document.getElementById('product-header');
    if (!header) return;
    formatReviewBadge();
    new MutationObserver(formatReviewBadge).observe(header, { childList: true, subtree: true });
  }

  function init() {
    syncNutritionPanel();
    mirrorHowToUse();
    watchPayoff();
    watchReviewBadge();
  }

  // supply-buybox-flavours.js dispatches this on the buy box container and it
  // bubbles, so listening at document level covers every section.
  document.addEventListener('supply-buybox:flavours-change', syncNutritionPanel);
  document.addEventListener('supply-buybox:flavours-change', syncTierImages);
  document.addEventListener('supply-buybox:flavours-change', syncPayoffSachet);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
